import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, AlertTriangle, Building2, Ambulance, X } from 'lucide-react';
import { offersApi, startPointsApi, patientsApi, OfferType, StartPoint, Patient } from '../../services/api';
import './OfferCreationModal.css';

interface OfferCreationModalProps {
  requestId: string;
  patientIds: string[];
  onClose: () => void;
  onSuccess: () => void;
  existingOffer?: any; // If editing existing offer
}

interface PatientStatistics {
  total: number;
  byStatus: { [key: string]: number };
  byPriority: { [key: string]: number };
  byDependency: { [key: string]: number };
  byTransport: { [key: string]: number };
  withCompanions: number;
  totalCompanions: number;
}

function OfferCreationModal({ requestId, patientIds, onClose, onSuccess, existingOffer }: OfferCreationModalProps) {
  const { t } = useTranslation();
  const isEditMode = !!existingOffer;

  const [offerType, setOfferType] = useState<OfferType>(existingOffer?.offerType || 'TREATMENT');
  const [startPoints, setStartPoints] = useState<StartPoint[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [_patients, setPatients] = useState<Patient[]>([]);
  const [statistics, setStatistics] = useState<PatientStatistics | null>(null);

  // Form fields
  const [receivingStartPointId, setReceivingStartPointId] = useState(existingOffer?.receivingStartPointId || '');
  const [comment, setComment] = useState(existingOffer?.comment || '');

  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userNation = currentUser.nation || '';

  useEffect(() => {
    loadStartPoints();
    loadPatients();
  }, []);

  // Reload start points when offer type changes
  useEffect(() => {
    loadStartPoints();
  }, [offerType]);

  const loadStartPoints = async () => {
    try {
      const response = await startPointsApi.getAll();

      // For TRANSPORT-only offers, show ALL UCPM hubs (no filtering)
      // For TREATMENT or BOTH offers, filter to own nation + assigned nations
      if (offerType === 'TRANSPORT') {
        setStartPoints(response.data);
      } else {
        const filteredStartPoints = response.data.filter((sp: StartPoint) =>
          sp.nation === userNation ||
          (sp.assignedNations && sp.assignedNations.includes(userNation))
        );
        setStartPoints(filteredStartPoints);
      }
    } catch (error) {
      console.error('Failed to load UCPM hubs:', error);
    }
  };

  const loadPatients = async () => {
    try {
      // Load all patients from request (without limit to get all)
      const response = await patientsApi.getByRequestId(requestId, {
        limit: 1000, // Large limit to ensure we get all patients
        page: 1
      });

      // Filter to only selected patients
      const selectedPatients = response.data.patients.filter((p: Patient) =>
        patientIds.includes(p.id)
      );
      setPatients(selectedPatients);

      // Calculate statistics
      calculateStatistics(selectedPatients);
    } catch (error) {
      console.error('Failed to load patients:', error);
    }
  };

  const calculateStatistics = (patientList: Patient[]) => {
    const stats: PatientStatistics = {
      total: patientList.length,
      byStatus: { CIVIL: 0, MILITARY: 0, TBA: 0 },
      byPriority: { P1: 0, P2: 0, P3: 0, TBA: 0 },
      byDependency: { LOW: 0, MEDIUM: 0, HIGH: 0, TBA: 0 },
      byTransport: { SEATED: 0, LYING: 0, TBA: 0 },
      withCompanions: 0,
      totalCompanions: 0
    };

    patientList.forEach(patient => {
      stats.byStatus[patient.patientStatus]++;
      stats.byPriority[patient.medicalPriority]++;
      stats.byDependency[patient.medicalDependency]++;
      stats.byTransport[patient.medicalTransport]++;

      if (patient.patientCompanionsCount > 0) {
        stats.withCompanions++;
        stats.totalCompanions += patient.patientCompanionsCount;
      }
    });

    setStatistics(stats);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (offerType === 'TREATMENT' || offerType === 'BOTH') {
      if (!receivingStartPointId) {
        alert('Please select a receiving UCPM hub');
        return;
      }
    }

    try {
      setSubmitting(true);

      const offerData: any = {
        offerType,
        patientIds,
      };

      // Add receiving UCPM hub (StartPoint)
      if (receivingStartPointId) {
        offerData.receivingStartPointId = receivingStartPointId;
      }

      // Add comment
      if (comment) offerData.comment = comment;

      if (isEditMode) {
        // Update existing offer
        await offersApi.update(existingOffer.id, offerData);
        alert('Offer updated successfully!');
      } else {
        // Create new offer
        offerData.requestId = requestId;
        await offersApi.create(offerData);
        alert('Offer created successfully!');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} offer:`, error);
      alert(error.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} offer`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="offer-modal-overlay">
      <div className="offer-modal">
        <div className="offer-modal-header">
          <h2>{isEditMode ? t('common.edit') : t('common.create')} {t('offers.title')} ({statistics ? statistics.total : patientIds.length} {t('patients.title').toLowerCase()} + {statistics ? statistics.totalCompanions : 0} comp.)</h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <form className="offer-modal-body" onSubmit={handleSubmit}>
          {/* Patient Statistics Overview */}
          {statistics && (
            <div className="offer-section">
              <div className="patient-stats-cards">
                {/* Status */}
                <div className="patient-stat-card card">
                  <div className="stat-card-icon"><Users size={20} /></div>
                  <div className="stat-card-content">
                    <div className="stat-card-values">
                      <span className="stat-card-item">{t('patients.stable')}: <strong>{statistics.byStatus.CIVIL}</strong></span>
                      <span className="stat-card-item">{t('patients.serious')}: <strong>{statistics.byStatus.MILITARY}</strong></span>
                    </div>
                  </div>
                </div>

                {/* Priority */}
                <div className="patient-stat-card card">
                  <div className="stat-card-icon"><AlertTriangle size={20} /></div>
                  <div className="stat-card-content">
                    <div className="stat-card-values">
                      <span className="stat-card-item priority-p1">P1: <strong>{statistics.byPriority.P1}</strong></span>
                      <span className="stat-card-item priority-p2">P2: <strong>{statistics.byPriority.P2}</strong></span>
                      <span className="stat-card-item priority-p3">P3: <strong>{statistics.byPriority.P3}</strong></span>
                    </div>
                  </div>
                </div>

                {/* Dependency */}
                <div className="patient-stat-card card">
                  <div className="stat-card-icon"><Building2 size={20} /></div>
                  <div className="stat-card-content">
                    <div className="stat-card-values">
                      <span className="stat-card-item">{t('patients.ambulatory')}: <strong>{statistics.byDependency.LOW}</strong></span>
                      <span className="stat-card-item">{t('patients.wheelchair')}: <strong>{statistics.byDependency.MEDIUM}</strong></span>
                      <span className="stat-card-item">{t('patients.stretcher')}: <strong>{statistics.byDependency.HIGH}</strong></span>
                    </div>
                  </div>
                </div>

                {/* Transport & Companions */}
                <div className="patient-stat-card card">
                  <div className="stat-card-icon"><Ambulance size={20} /></div>
                  <div className="stat-card-content">
                    <div className="stat-card-values">
                      <span className="stat-card-item">{t('patients.seated')}: <strong>{statistics.byTransport.SEATED}</strong></span>
                      <span className="stat-card-item">{t('patients.lying')}: <strong>{statistics.byTransport.LYING}</strong></span>
                      <span className="stat-card-item">+{t('patients.accompaniment', 'Comp')}: <strong>{statistics.withCompanions}</strong></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Offer Type Selection */}
          <div className="offer-section">
            <label className="section-label">{t('offers.offerType')} *</label>
            <div className="offer-type-selector">
              <button
                type="button"
                className={`offer-type-btn ${offerType === 'TREATMENT' ? 'active' : ''}`}
                onClick={() => setOfferType('TREATMENT')}
              >
                <div className="offer-type-icon"><Building2 size={22} /></div>
                <div className="offer-type-label">{t('offers.treatment')}</div>
              </button>
              <button
                type="button"
                className={`offer-type-btn ${offerType === 'TRANSPORT' ? 'active' : ''}`}
                onClick={() => setOfferType('TRANSPORT')}
              >
                <div className="offer-type-icon"><Ambulance size={22} /></div>
                <div className="offer-type-label">{t('offers.transport')}</div>
              </button>
              <button
                type="button"
                className={`offer-type-btn ${offerType === 'BOTH' ? 'active' : ''}`}
                onClick={() => setOfferType('BOTH')}
              >
                <div className="offer-type-icon-both">
                  <span><Building2 size={18} /></span>
                  <span><Ambulance size={18} /></span>
                </div>
                <div className="offer-type-label">{t('offers.combined')}</div>
              </button>
            </div>
          </div>

          {/* UCPM Hub Selection */}
          <div className="offer-section">
            <label className="section-label">Receiving UCPM Hub {(offerType === 'TRANSPORT') ? '(optional)' : '*'}</label>
            <div className="form-group">
              <select
                value={receivingStartPointId}
                onChange={(e) => setReceivingStartPointId(e.target.value)}
                required={offerType === 'TREATMENT' || offerType === 'BOTH'}
              >
                <option value="">Select UCPM hub{offerType === 'TRANSPORT' ? ' (optional)' : ''}...</option>
                {startPoints.map(sp => (
                  <option key={sp.id} value={sp.id}>
                    {sp.name} ({sp.nation})
                  </option>
                ))}
              </select>
              {offerType === 'TRANSPORT' && (
                <small className="field-hint">Can be negotiated later if not specified</small>
              )}
            </div>
          </div>

          {/* Comment */}
          <div className="offer-section">
            <label className="section-label">{t('common.comments')} ({t('common.optional')})</label>
            <textarea
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add any additional comments or information for the requesting nation..."
              className="comment-textarea"
            />
          </div>

          {/* Actions */}
          <div className="offer-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-success" disabled={submitting}>
              {submitting
                ? (isEditMode ? `${t('common.update')}...` : `${t('offers.submitting')}`)
                : (isEditMode ? t('common.update') : t('offers.createOffer'))
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OfferCreationModal;
